import { ModuleFE_Advisor_Expression, ModuleFE_Advisor_MeasurementUnit, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_AdvisorSession, ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { findingSourceTypesEnum, FindingType_Enumerated, FindingType_Numeric, FindingType_Range, FindingType_Textual } from '@app/advisor-shared';
import { ExpressionType_NormalRange, resolveValueDisplay, valueType_Range, valueType_StandardRange, variableType_AttributeInstance } from '@app/hcs-shared';
import { Logger } from '@nu-art/logger';
import { exists, filterInstances, sortArray } from '@nu-art/ts-common';
export class FindingData extends Logger {
    static modulesToAwait = [
        //Static Modules
        ModuleFE_Advisor_Variable,
        ModuleFE_Advisor_Value,
        ModuleFE_Advisor_MeasurementUnit,
        ModuleFE_Advisor_Expression,
        //Runtime Modules
        ModuleFE_RuntimeFinding,
    ];
    static numberValueFormatter = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
    finding;
    variable;
    value;
    measurementUnit;
    normalRange;
    static tooltipModules = {
        variable: ModuleFE_Advisor_Variable,
        value: ModuleFE_Advisor_Value,
        measurementUnit: ModuleFE_Advisor_MeasurementUnit,
    };
    static createNew = (_finding) => {
        const finding = typeof _finding === 'string' ? ModuleFE_RuntimeFinding.cache.unique(_finding) : _finding;
        if (!finding || finding.type === FindingType_Range)
            return;
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable)
            return;
        return new FindingData(finding, variable);
    };
    constructor(finding, variable) {
        super(`Finding Data - ${variable.name}`);
        this.finding = finding;
        this.variable = variable;
        //Resolve value
        this.value = this.resolveValueString(this.finding);
        //Resolve measurement unit
        this.measurementUnit = this.resolveMeasurementUnit(this.variable);
        //Resolve normal range
        this.normalRange = this.resolveNormalRange(this.variable);
    }
    //######################### Inner Logic #########################
    resolveValueString(finding) {
        switch (finding.type) {
            case FindingType_Textual:
                return finding.value;
            case FindingType_Numeric:
                return FindingData.numberValueFormatter.format(finding.value);
            case FindingType_Enumerated: {
                const value = ModuleFE_Advisor_Value.cache.unique(finding.valueIds[0]);
                return value ? resolveValueDisplay(value) : 'N/A';
            }
            default:
                return 'N/A';
        }
    }
    resolveMeasurementUnit(variable) {
        const value = ModuleFE_Advisor_Value.cache.unique(variable.valueId);
        if (value && [valueType_Range, valueType_StandardRange].includes(value.type))
            return ModuleFE_Advisor_MeasurementUnit.cache.unique(value.data.unitsId);
    }
    resolveNormalRange(variable) {
        //Find a "normal range" expression for this variable
        const expression = ModuleFE_Advisor_Expression.cache.find(exp => {
            return exp.expressionType === ExpressionType_NormalRange.dbKey
                && exp.associatedVar === variable._id;
        });
        if (!expression)
            return;
        //Get latest calculation result for the normal range
        const findings = ModuleFE_RuntimeFinding.cache.filter(finding => finding.variableId === expression._id);
        const latest = sortArray(findings, f => f.__created, true)[0];
        //Return range if it exists
        const range = latest?.value;
        return (!range || !exists(range[0]) || !exists(range[1])) ? undefined : range;
    }
    resolveFindingTooltipResults() {
        const findings = ModuleFE_RuntimeFinding.cache.filter(f => f.type === this.finding.type && f.variableId === this.finding.variableId);
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        return findings.map(f => {
            const result = {
                currentEncounter: f.encounterId === currentEncounterId,
                timestamp: f.issuedTimestamp ?? f.__updated ?? f.__created,
            };
            switch (f.type) {
                case FindingType_Textual:
                    result.rawValue = f.value;
                    break;
                case FindingType_Enumerated:
                    result.valueId = f.valueIds[0];
                    //Translate Finding Attributes
                    result.attributes = filterInstances(f.attributes?.map(attribute => {
                        const attVar = ModuleFE_Advisor_Variable.cache.unique(attribute.variableId);
                        if (!attVar)
                            return;
                        return {
                            variable: attVar,
                            results: [{
                                    valueId: attribute.valueIds[0],
                                    timestamp: result.timestamp,
                                    currentEncounter: result.currentEncounter,
                                }],
                            metadata: {},
                        };
                    }));
                    break;
                case FindingType_Numeric:
                    result.rawValue = f.value;
            }
            return result;
        });
    }
    //######################### Public Logic #########################
    getLabel = () => {
        if (this.variable.type === variableType_AttributeInstance)
            return this.variable.name.split('.')[1];
        return this.variable.name;
    };
    getValue = (withMeasurementUnit = false) => {
        if (!withMeasurementUnit || !this.measurementUnit)
            return this.value;
        return `${this.value} ${this.measurementUnit.unitAsString}`;
    };
    getTooltipData = () => {
        const source = this.finding.source.type === findingSourceTypesEnum.SMARTDOC ? undefined : this.finding.source.type;
        return {
            variable: this.variable,
            results: this.resolveFindingTooltipResults(),
            metadata: {
                measurementUnitId: this.measurementUnit?._id,
                source: source,
                varNormal: this.normalRange,
            }
        };
    };
    isAbnormal = () => {
        if (!this.normalRange)
            return false; //Finding can't be abnormal with a lack of normal range
        if (this.finding.type !== FindingType_Numeric)
            return false; //Abnormality is only defined for numeric values
        const value = this.finding.value;
        return value < this.normalRange[0] || value > this.normalRange[1];
    };
}
//# sourceMappingURL=FindingData.js.map